/* eslint-disable @typescript-eslint/no-explicit-any */
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import useU2000Login from "../../u2000Core/hooks/authentification/U2000UseLogin";
import useU2000LoginMethod from "../../u2000Core/hooks/authentification/U2000UseLoginMethod";
import useU2000MfaAuthenticate from "../../u2000Core/hooks/authentification/U2000UseMfaAuthenticate";
import useU2000ToggleLocalStorage from "../../u2000Core/hooks/U2000UseToggleLocalStorage";
import { u2000Menu } from "../../u2000Core/menu/U2000Menu";
import { U2000MenuFindModuleFromPath } from "../../u2000Core/menu/U2000MenuUtils";
import useU2000ChangePassword from "../../u2000Core/hooks/authentification/U2000UseChangePassword";
import useU2000ResetPassword from "../../u2000Core/hooks/authentification/U2000UseResetPassword";
import { jwtDecode } from "jwt-decode";
import { NS } from "../U2001";
import { FaEye, FaEyeSlash } from "react-icons/fa6";
import useU2000SelectOrganization from "../../u2000Core/hooks/authentification/U2000UseSelectOrganization";
import U2000Label from "../../u2000Core/components/formControls/label/U2000Label";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import U2000PasswordProgressionPopup from "../../u2000Core/components/popup/U2000PasswordProgressionPopup";
import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import U2000OrganizationList from "../../u2000Core/components/U2000OrganizationList";
import { u2001MapLoginMethodError, u2001MapSsoErrorKey, u2001MapSsoPasswordPathDisabled } from "../tools/U2001SsoLoginHelpers";

interface U2001InputFieldsProps {
  fieldName: string;
  inputType?: string;
  label: string;
  value: string;
  setValue: (e: React.ChangeEvent<HTMLInputElement>) => void;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  onFocus?: () => void;
  onBlur?: () => void;
  autoComplete?: string;
  placeholder?: string;
}

const U2001InputFields = ({
  fieldName,
  inputType,
  label,
  value,
  setValue,
  inputRef,
  onFocus,
  onBlur,
  autoComplete,
  placeholder,
}: U2001InputFieldsProps) => {
  const [onFieldInput, setOnFieldInput] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);

  const getInputClasses = () => {
    const baseClasses =
      "w-full h-[46px] px-4 text-sm bg-surface border rounded-lg transition-all duration-200 focus:outline-hidden focus:ring-2";

    if (onFieldInput) {
      return `${baseClasses} border-blue-500 ring-blue-500/20 text-gray-900`;
    }

    return `${baseClasses} border-gray-300 hover:border-gray-400 text-gray-900`;
  };

  const handleFocus = () => {
    setOnFieldInput(true);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setOnFieldInput(false);
    if (onBlur) onBlur();
  };

  const resolvedAutoComplete =
    autoComplete ?? (inputType === "password" ? "new-password" : "username");

  return (
    <fieldset>
      <U2000Label label={label} />
      <div className="relative flex w-full items-start">
        <input
          ref={inputRef}
          type={(showLoginPassword ? "text" : inputType) || "text"}
          className={getInputClasses()}
          id={fieldName}
          name={fieldName}
          value={value}
          onChange={setValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoComplete={resolvedAutoComplete}
          placeholder={placeholder}
        />
        {inputType === "password" && (
          <button
            type="button"
            aria-pressed={showLoginPassword}
            aria-label={showLoginPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
            className="absolute top-1/2 right-3 -translate-y-1/2 flex items-center justify-center w-8 h-8 
                       text-gray-400 hover:text-gray-600 rounded transition-colors duration-200
                       focus:outline-hidden focus:ring-2 focus:ring-blue-500/20"
            onClick={() => setShowLoginPassword((v) => !v)}
          >
            {showLoginPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        )}
      </div>
    </fieldset>
  );
};

const btnClass = `flex flex-row items-center justify-center h-[46px] rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white shadow-xs outline-offset-2 transition-all duration-200 select-none hover:bg-primary-700 focus:outline-hidden focus:ring-2 focus:ring-primary-500/20 active:scale-95 disabled:cursor-not-allowed disabled:bg-gray-400 disabled:text-gray-200 disabled:opacity-60`;

interface Organization {
  uuid: string;
  organizationName: string;
}

interface LoginResponse {
  isAuthenticated: boolean;
  changePasswordOnLogin?: boolean;
  organizations?: Organization[];
  token?: string;
  mfaRequired?: boolean;
  mfaChallengeToken?: string | null;
}

/** Helpers org persistée (partagés login / MFA). */
const getPersistedOrganizationForUser = (token: string) => {
  try {
    const userContext: any = jwtDecode(token);
    const key = `U2001.lastOrganization.${userContext.sub}`;
    const storedData = localStorage.getItem(key);
    if (storedData) {
      return JSON.parse(storedData);
    }
  } catch (error) {
    console.warn("Error reading persisted organization:", error);
  }
  return null;
};

const clearPersistedOrganizationForUser = (token: string) => {
  try {
    const userContext: any = jwtDecode(token);
    const key = `U2001.lastOrganization.${userContext.sub}`;
    localStorage.removeItem(key);
  } catch (error) {
    console.warn("Error clearing persisted organization:", error);
  }
};

type PendingAutoSelection = {
  organizations: Organization[];
  persistedOrganization: any;
};

/** Après authentification complète (MDP, MFA ou SSO) — navigation changePassword / orgs / home. */
export const applyAuthenticatedLoginResponse = (
  response: LoginResponse,
  options: {
    setScreenStateFn: (state: string) => void;
    setOrganizations: Dispatch<SetStateAction<Organization[]>>;
    goToHomePageFn: () => void;
    setPendingAutoSelection: Dispatch<SetStateAction<PendingAutoSelection | null>>;
  },
): void => {
  const { setScreenStateFn, setOrganizations, goToHomePageFn, setPendingAutoSelection } = options;
  if (response.changePasswordOnLogin) {
    setScreenStateFn("changePassword");
    return;
  }
  if (response.organizations && response.organizations.length > 1) {
    const persistedOrganization = response.token ? getPersistedOrganizationForUser(response.token) : null;
    if (persistedOrganization) {
      const orgExists = response.organizations.find((org) => org.uuid === persistedOrganization.uuid);
      if (orgExists) {
        setPendingAutoSelection({
          organizations: response.organizations,
          persistedOrganization,
        });
        return;
      }
      if (response.token) {
        clearPersistedOrganizationForUser(response.token);
      }
    }
    setOrganizations(response.organizations);
    setScreenStateFn("selectOrganization");
    return;
  }
  goToHomePageFn();
};

const mapLoginMethodError = u2001MapLoginMethodError;

interface UsernameScreenProps {
  setScreenStateFn: (state: string) => void;
  setUserNameFn: (username: string) => void;
  username: string;
  setUsername: (username: string) => void;
  initialError?: string;
}

/** BL-0520 — Écran 1 : nom d'usager + loginMethod. */
const UsernameScreen = ({
  setScreenStateFn,
  setUserNameFn,
  username,
  setUsername,
  initialError,
}: UsernameScreenProps) => {
  const { t } = useTranslation();
  const doLoginMethod = useU2000LoginMethod();
  const userRef = useRef<HTMLInputElement>(null);

  const [check, setCheck] = useU2000ToggleLocalStorage("persist", false);
  const [errMsg, setErrMsg] = useState<string>(initialError || "");
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    userRef.current?.focus();
  }, []);

  useEffect(() => {
    if (initialError) {
      setErrMsg(initialError);
    }
  }, [initialError]);

  const handleSubmitUsername = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed) {
      setErrMsg(t("login.msgUsernameRequired", { ns: NS }));
      return;
    }
    setLoading(true);
    setErrMsg("");
    try {
      const result = await doLoginMethod(trimmed);
      localStorage.setItem("U2001.username", trimmed);
      setUserNameFn(trimmed);
      setUsername(trimmed);

      if (result.method === "SSO_GOOGLE" && result.authorizeUrl) {
        window.location.assign(result.authorizeUrl);
        return;
      }
      setScreenStateFn("saisieMotDePasse");
    } catch (error: any) {
      setErrMsg(mapLoginMethodError(error, t));
      userRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="w-full text-center text-2xl leading-tight font-semibold text-gray-900">
            {t("lblWelcomBack", { ns: NS })}
          </div>
        </div>
      </div>
      {errMsg && <div className="text-center text-sm font-medium text-red-600">{errMsg}</div>}
      <div className="flex w-full flex-col gap-6">
        <form onSubmit={handleSubmitUsername} className="flex w-full flex-col gap-6">
          <U2001InputFields
            fieldName="username"
            label={t("lblUsername", { ns: NS })}
            value={username}
            setValue={(e) => setUsername(e.target.value)}
            inputRef={userRef}
            autoComplete="username"
            placeholder={t("login.placeholderUsername", { ns: NS })}
          />

          <div className="flex w-full flex-col items-start justify-between gap-3 sm:flex-row sm:items-center sm:gap-0">
            <div className="flex items-center gap-3">
              <input
                id="rememberme1"
                type="checkbox"
                className="h-4 w-4 text-blue-600 bg-surface border-gray-300 rounded 
                           focus:ring-blue-500 focus:ring-2 transition-all duration-200"
                onChange={() => setCheck(!check)}
                checked={check}
              />
              <label htmlFor="rememberme1" className="text-sm text-gray-700 leading-relaxed select-none cursor-pointer">
                {t("lblRememberMe", { ns: NS })}
              </label>
            </div>
          </div>
          <button className={btnClass} type="submit" disabled={loading}>
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {loading ? t("lblConnecting", { ns: NS }) : t("login.btnNext", { ns: NS })}
          </button>
        </form>
      </div>
    </>
  );
};

interface PasswordScreenProps {
  setScreenStateFn: (state: string) => void;
  setUserNameFn: (username: string) => void;
  setOrganizations: Dispatch<SetStateAction<Organization[]>>;
  goToHomePageFn: () => void;
  onMfaRequired: (mfaChallengeToken: string) => void;
  username: string;
}

/** BL-0520 — Écran 2 : mot de passe uniquement (parcours PASSWORD). */
const PasswordScreen = ({
  setScreenStateFn,
  setUserNameFn,
  setOrganizations,
  goToHomePageFn,
  onMfaRequired,
  username,
}: PasswordScreenProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { auth } = useU2000AuthContext();
  const doLogin = useU2000Login();
  const doSelectOrganization = useU2000SelectOrganization();
  const passwordRef = useRef<HTMLInputElement>(null);

  const [password, setPassword] = useState("");
  const [errMsg, setErrMsg] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [pendingAutoSelection, setPendingAutoSelection] = useState<PendingAutoSelection | null>(null);

  useEffect(() => {
    passwordRef.current?.focus();
  }, []);

  useEffect(() => {
    if (pendingAutoSelection && auth.token && auth.isAuthenticated) {
      const { organizations, persistedOrganization } = pendingAutoSelection;
      const handleAutoSelection = async () => {
        try {
          const orgResponse = await doSelectOrganization(persistedOrganization.uuid, u2000Menu);
          if (orgResponse.isAuthenticated) {
            toast.success(
              t("organizationAutoSelected", { organizationName: persistedOrganization.organizationName, ns: NS }),
              t("successTitle", { ns: "common" }),
              3000,
            );
            goToHomePageFn();
          }
        } catch {
          if (auth.token) {
            clearPersistedOrganizationForUser(auth.token);
          }
          toast.warning(t("autoSelectFailed", { ns: NS }), t("warningTitle", { ns: "common" }), 4000);
          setOrganizations(organizations);
          setScreenStateFn("selectOrganization");
        } finally {
          setPendingAutoSelection(null);
        }
      };

      handleAutoSelection();
    }
  }, [
    auth.token,
    auth.isAuthenticated,
    pendingAutoSelection,
    doSelectOrganization,
    toast,
    t,
    goToHomePageFn,
    setOrganizations,
    setScreenStateFn,
  ]);

  const handleSubmitLogin = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!username || !password) {
      setErrMsg(t("msgRequiredFields", { ns: NS }));
      return;
    }
    setLoading(true);
    try {
      const response: LoginResponse = await doLogin(username, password, u2000Menu);
      if (response.mfaRequired === true && response.mfaChallengeToken) {
        localStorage.setItem("U2001.username", username);
        setUserNameFn(username);
        onMfaRequired(response.mfaChallengeToken);
        return;
      }
      if (response.isAuthenticated) {
        localStorage.setItem("U2001.username", username);
        setUserNameFn(username);
        applyAuthenticatedLoginResponse(response, {
          setScreenStateFn,
          setOrganizations,
          goToHomePageFn,
          setPendingAutoSelection,
        });
      }
    } catch (error: any) {
      const ssoMsg = u2001MapSsoPasswordPathDisabled(error, t);
      if (ssoMsg) {
        setErrMsg(ssoMsg);
      } else if (error.status === 400) {
        setErrMsg(t("msgAuthorizationFailed", { ns: NS }));
      } else if (error.status === 401) {
        const errorList = error?.data?.errors || [];
        const isLocked = errorList.some((e: { errorCode: string }) => e.errorCode === "A0003");
        if (isLocked) {
          setErrMsg(t("msgAccountLocked", { ns: NS }));
        } else {
          setErrMsg(t("msgInvalidCredentials", { ns: NS }));
        }
      } else {
        setErrMsg(t("msgUnexpectedError", { ns: NS }));
      }
      passwordRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setScreenStateFn("resetPassword");
  };

  const handleChangeUser = (e?: React.MouseEvent) => {
    e?.preventDefault();
    setScreenStateFn("saisieUsager");
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="w-full text-center text-2xl leading-tight font-semibold text-gray-900">
            {t("lblWelcomBack", { ns: NS })}
          </div>
          <div className="w-full text-center text-sm text-gray-700">
            {username}{" "}
            <button
              type="button"
              className="font-medium text-blue-600 hover:text-blue-700 underline"
              onClick={handleChangeUser}
            >
              {t("login.btnChangeUser", { ns: NS })}
            </button>
          </div>
        </div>
      </div>
      {errMsg && <div className="text-center text-sm font-medium text-red-600">{errMsg}</div>}
      <div className="flex w-full flex-col gap-6">
        <form onSubmit={handleSubmitLogin} className="flex w-full flex-col gap-6">
          {/* Champ username caché pour autofill / gestionnaires de mots de passe */}
          <input type="text" name="username" autoComplete="username" value={username} readOnly className="sr-only" tabIndex={-1} aria-hidden />
          <U2001InputFields
            fieldName="password"
            label={t("lblPassword", { ns: NS })}
            inputType="password"
            value={password}
            setValue={(e) => setPassword(e.target.value)}
            inputRef={passwordRef}
            autoComplete="current-password"
            placeholder={t("login.placeholderPassword", { ns: NS })}
          />

          <div className="flex w-full justify-end">
            <button
              type="button"
              className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors duration-200 
                         focus:outline-hidden focus:ring-2 focus:ring-blue-500/20 rounded px-1"
              onClick={handleForgotPassword}
            >
              {t("lblForgotPassword", { ns: NS })}
            </button>
          </div>
          <button className={btnClass} type="submit" disabled={loading}>
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {loading ? t("lblConnecting", { ns: NS }) : t("lblConnect", { ns: NS })}
          </button>
        </form>
      </div>
    </>
  );
};

interface MfaScreenProps {
  mfaChallengeToken: string;
  setScreenStateFn: (state: string) => void;
  setOrganizations: Dispatch<SetStateAction<Organization[]>>;
  goToHomePageFn: () => void;
  onClearMfa: () => void;
}

/** BL-0413 — saisie code TOTP ou code de secours. */
const MfaScreen = ({ mfaChallengeToken, setScreenStateFn, setOrganizations, goToHomePageFn, onClearMfa }: MfaScreenProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { auth } = useU2000AuthContext();
  const doMfaAuthenticate = useU2000MfaAuthenticate();
  const doSelectOrganization = useU2000SelectOrganization();
  const codeRef = useRef<HTMLInputElement>(null);

  const [code, setCode] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingAutoSelection, setPendingAutoSelection] = useState<PendingAutoSelection | null>(null);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  useEffect(() => {
    if (pendingAutoSelection && auth.token && auth.isAuthenticated) {
      const { organizations, persistedOrganization } = pendingAutoSelection;
      const handleAutoSelection = async () => {
        try {
          const orgResponse = await doSelectOrganization(persistedOrganization.uuid, u2000Menu);
          if (orgResponse.isAuthenticated) {
            toast.success(
              t("organizationAutoSelected", { organizationName: persistedOrganization.organizationName, ns: NS }),
              t("successTitle", { ns: "common" }),
              3000,
            );
            goToHomePageFn();
          }
        } catch {
          if (auth.token) {
            clearPersistedOrganizationForUser(auth.token);
          }
          toast.warning(t("autoSelectFailed", { ns: NS }), t("warningTitle", { ns: "common" }), 4000);
          setOrganizations(organizations);
          setScreenStateFn("selectOrganization");
        } finally {
          setPendingAutoSelection(null);
        }
      };
      handleAutoSelection();
    }
  }, [auth.token, auth.isAuthenticated, pendingAutoSelection, doSelectOrganization, toast, t, goToHomePageFn, setOrganizations, setScreenStateFn]);

  const handleReturnToLogin = () => {
    onClearMfa();
    setScreenStateFn("saisieUsager");
  };

  const handleSubmitMfa = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) {
      setErrMsg(t("login.mfa.required", { ns: NS }));
      return;
    }
    if (!mfaChallengeToken) {
      setErrMsg(t("msgUnexpectedError", { ns: NS }));
      return;
    }
    setLoading(true);
    setErrMsg("");
    try {
      const response: LoginResponse = await doMfaAuthenticate(mfaChallengeToken, trimmed, u2000Menu);
      if (response.isAuthenticated) {
        onClearMfa();
        applyAuthenticatedLoginResponse(response, {
          setScreenStateFn,
          setOrganizations,
          goToHomePageFn,
          setPendingAutoSelection,
        });
      }
    } catch (error: any) {
      const ssoMsg = u2001MapSsoPasswordPathDisabled(error, t);
      if (ssoMsg) {
        setErrMsg(ssoMsg);
      } else if (error.status === 401) {
        const errorList = error?.data?.errors || [];
        const isLocked = errorList.some((err: { errorCode: string }) => err.errorCode === "A0003");
        if (isLocked) {
          setErrMsg(t("msgAccountLocked", { ns: NS }));
        } else {
          setErrMsg(t("login.mfa.invalid", { ns: NS }));
        }
      } else if (error.status === 400) {
        setErrMsg(t("login.mfa.invalid", { ns: NS }));
      } else {
        setErrMsg(t("msgUnexpectedError", { ns: NS }));
      }
      codeRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="w-full text-center text-2xl leading-tight font-semibold text-gray-900">
            {t("login.mfa.title", { ns: NS })}
          </div>
          <div className="w-full text-center text-gray-600">{t("login.mfa.hint", { ns: NS })}</div>
        </div>
      </div>
      {errMsg && <div className="text-center text-sm font-medium text-red-600">{errMsg}</div>}
      <div className="flex w-full flex-col gap-6">
        <form className="flex w-full flex-col gap-6" onSubmit={handleSubmitMfa}>
          <U2001InputFields
            fieldName="mfaCode"
            label={t("login.mfa.codeLabel", { ns: NS })}
            value={code}
            setValue={(e) => setCode(e.target.value)}
            inputRef={codeRef}
            autoComplete="one-time-code"
          />
          <button className={btnClass} type="submit" disabled={loading}>
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {loading ? t("login.mfa.submitting", { ns: NS }) : t("login.mfa.submit", { ns: NS })}
          </button>
          <div className="mt-2 flex w-full justify-center">
            <button type="button" className="cursor-pointer font-medium text-black underline hover:text-black" onClick={handleReturnToLogin}>
              {t("lblReturnToLogin", { ns: NS })}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const ChangePasswordScreen = ({ setScreenStateFn }: { setScreenStateFn: (state: string) => void }) => {
  const { t } = useTranslation();
  const doChangePassword = useU2000ChangePassword();
  const [changePassword, setChangePassword] = useState({ newPassword: "", confirmPassword: "" });
  const [changeMsg, setChangeMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPasswordPopup, setShowPasswordPopup] = useState(false);

  const handleChangePasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const { newPassword, confirmPassword } = changePassword;
    if (!newPassword || !confirmPassword) {
      setChangeMsg(t("msgRequiredNewPasswordAndConfirmPassword", { ns: NS }));
      return;
    }
    if (newPassword !== confirmPassword) {
      setChangeMsg(t("msgPasswordsDoNotMatch", { ns: NS }));
      return;
    }
    setLoading(true);

    try {
      await doChangePassword(newPassword, confirmPassword);
      setChangeMsg(t("msgPasswordChangedSuccessfully", { ns: NS }));
      setChangePassword({ newPassword: "", confirmPassword: "" });
      setScreenStateFn("saisieUsager");
    } catch (error: any) {
      const ssoMsg = u2001MapSsoPasswordPathDisabled(error, t);
      if (ssoMsg) {
        setChangeMsg(ssoMsg);
      } else if (error.status === 400) {
        const errorList = error?.data?.errors || [];
        const isAlreadyUsed = errorList.some((e: { errorCode?: string }) => e.errorCode === "BU2001");
        if (isAlreadyUsed) {
          setChangeMsg(t("msgPasswordAlreadyUsed", { ns: NS }));
        } else {
          setChangeMsg(t("msgChangePasswordError", { ns: NS }));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="w-full text-center text-2xl leading-tight font-semibold text-gray-900">{t("lblChangePasswordTitle", { ns: NS })}</div>
          <div className="w-full text-center text-gray-600">{t("lblChangePasswordSubTitle", { ns: NS })}</div>
        </div>
      </div>
      {changeMsg && <div className="text-center text-sm font-medium text-red-600">{changeMsg}</div>}
      <div className="flex w-full flex-col gap-6">
        <form className="flex w-full flex-col gap-6" onSubmit={handleChangePasswordSubmit}>
          <div className="relative">
            <U2001InputFields
              fieldName="newPassword"
              inputType="password"
              label={t("lblNewPassword", { ns: NS })}
              value={changePassword.newPassword}
              setValue={(e) => setChangePassword((prev) => ({ ...prev, newPassword: e.target.value }))}
              onFocus={() => setShowPasswordPopup(true)}
              onBlur={() => setShowPasswordPopup(false)}
              autoComplete="new-password"
            />
            <U2000PasswordProgressionPopup password={changePassword.newPassword} show={showPasswordPopup} position="RIGHT_CENTER" />
          </div>
          <U2001InputFields
            fieldName="confirmPassword"
            inputType="password"
            label={t("lblConfirmPassword", { ns: NS })}
            value={changePassword.confirmPassword}
            setValue={(e) => setChangePassword((prev) => ({ ...prev, confirmPassword: e.target.value }))}
            autoComplete="new-password"
          />

          <button className={btnClass} type="submit" disabled={loading}>
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {loading ? t("lblPasswordChanging", { ns: NS }) : t("lblChangePassword", { ns: NS })}
          </button>
          <div className="mt-2 flex w-full justify-center">
            <button type="button" className="cursor-pointer font-medium text-black underline hover:text-black" onClick={() => setScreenStateFn("saisieUsager")}>
              {t("lblReturnToLogin", { ns: NS })}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

const ResetPasswordScreen = ({ setScreenStateFn }: { setScreenStateFn: (state: string) => void }) => {
  const { t } = useTranslation();
  const doResetPassword = useU2000ResetPassword();
  const [resetPassword, setResetPassword] = useState({ username: "", secretKey: "" });
  const [resetMsg, setResetMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleResetPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setResetPassword((prev) => ({ ...prev, [name]: value }));
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!resetPassword.username || !resetPassword.secretKey) {
      setResetMsg(t("msgRequiredUserNameAndSecretKey", { ns: NS }));
      return;
    }
    setLoading(true);
    try {
      await doResetPassword(resetPassword.username, resetPassword.secretKey);
      setResetMsg(t("msgResetPasswordSuccess", { ns: NS }));
      setResetPassword({ username: "", secretKey: "" });
      setScreenStateFn("changePassword");
    } catch (error: any) {
      const ssoMsg = u2001MapSsoPasswordPathDisabled(error, t);
      if (ssoMsg) {
        setResetMsg(ssoMsg);
      } else if (error.status === 400) {
        setResetMsg(t("msgResetPasswordError", { ns: NS }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex flex-col items-center gap-4">
        <div className="flex w-full flex-col items-center gap-2">
          <div className="w-full text-center text-2xl leading-tight font-semibold text-gray-900">{t("lblResetPasswordTitle", { ns: NS })}</div>
          <div className="w-full text-center text-gray-600">{t("lblResetPasswordSubTitle", { ns: NS })}</div>
        </div>
      </div>
      {resetMsg && <div className="text-center text-sm font-medium text-red-600">{resetMsg}</div>}
      <div className="flex w-full flex-col gap-6">
        <form className="flex w-full flex-col gap-6" onSubmit={handleResetPasswordSubmit}>
          <U2001InputFields fieldName="username" label={t("lblUsername", { ns: NS })} value={resetPassword.username} setValue={(e) => handleResetPasswordChange(e)} autoComplete="username" />
          <U2001InputFields fieldName="secretKey" label={t("lblSecretKey", { ns: NS })} value={resetPassword.secretKey} setValue={(e) => handleResetPasswordChange(e)} autoComplete="off" />

          <button className={btnClass} type="submit" disabled={loading}>
            <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
            {loading ? t("lblResetingPassword", { ns: NS }) : t("lblResetPassword", { ns: NS })}
          </button>
          <div className="mt-2 flex w-full justify-center">
            <button type="button" className="cursor-pointer font-medium text-black underline hover:text-black" onClick={() => setScreenStateFn("saisieUsager")}>
              {t("lblReturnToLogin", { ns: NS })}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

interface SelectOrganizationScreenProps {
  setScreenStateFn: (state: string) => void;
  organizations: Organization[];
  goToHomePageFn: () => void;
}

const SelectOrganizationScreen = ({ setScreenStateFn, organizations, goToHomePageFn }: SelectOrganizationScreenProps) => {
  const { t } = useTranslation();
  const toast = useToast();
  const doSelectOrganization = useU2000SelectOrganization();
  const [errMsg, setErrMsg] = useState("");

  const handleSelect = async (org: Organization) => {
    try {
      const response = await doSelectOrganization(org.uuid, u2000Menu);
      if (response.isAuthenticated) {
        toast.success(t("organizationSelected", { organizationName: org.organizationName, ns: NS }), t("successTitle", { ns: "common" }), 3000);
        goToHomePageFn();
      }
    } catch (error: any) {
      if (error.status === 404) {
        setErrMsg(t("organizationNotFound", { ns: NS }));
        toast.error(t("organizationNotFound", { ns: NS }), t("errorTitle", { ns: "common" }), 5000);
      } else if (error.status === 400) {
        setErrMsg(t("msgAuthorizationFailed", { ns: NS }));
      } else {
        setErrMsg(t("msgUnexpectedError", { ns: NS }));
      }
    }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="text-center text-2xl font-semibold text-gray-900">{t("lblSelectOrganization", { ns: NS })}</div>
      {errMsg && <div className="text-center text-sm font-medium text-red-600">{errMsg}</div>}
      <U2000OrganizationList organizations={organizations} onSelect={handleSelect} />

      <div className="mt-2 flex w-full justify-center">
        <button type="button" className="cursor-pointer font-medium text-black underline hover:text-black" onClick={() => setScreenStateFn("saisieUsager")}>
          {t("lblReturnToLogin", { ns: NS })}
        </button>
      </div>
    </div>
  );
};

export type U2001LoginLocationState = {
  screenState?: string;
  organizations?: Organization[];
  ssoError?: string;
};

const mapSsoErrorKey = u2001MapSsoErrorKey;

const U2001Login = () => {
  const [, setUserName] = useState("");
  const { setCurrentModule, markAsNewLogin } = useU2000AuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const locationState = (location.state as U2001LoginLocationState | null) ?? null;

  const [screenState, setScreenState] = useState(locationState?.screenState || "saisieUsager");
  const [organizations, setOrganizations] = useState<Organization[]>(locationState?.organizations || []);
  const [mfaChallengeToken, setMfaChallengeToken] = useState("");
  const [username, setUsername] = useState(() => localStorage.getItem("U2001.username") || "");
  const [ssoErrorMsg, setSsoErrorMsg] = useState(() => {
    if (locationState?.ssoError) {
      return t(mapSsoErrorKey(locationState.ssoError), { ns: NS });
    }
    return "";
  });

  useEffect(() => {
    if (locationState) {
      navigate(location.pathname, { replace: true, state: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- consommer le state une seule fois au montage
  }, []);

  const goToHomePage = () => {
    markAsNewLogin();
    const currentModule = U2000MenuFindModuleFromPath(u2000Menu, "/U3400");
    setCurrentModule(currentModule);
    navigate("/", { replace: true });
  };

  const handleMfaRequired = (token: string) => {
    setMfaChallengeToken(token);
    setScreenState("mfa");
  };

  const handleClearMfa = () => {
    setMfaChallengeToken("");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-6 py-20 md:px-12 lg:px-20">
      <div className={`mx-auto flex w-full flex-col gap-8 rounded-2xl bg-gray-200 p-8 shadow-xs md:p-12 ${screenState === "selectOrganization" ? "max-w-3xl" : "max-w-xl"}`}>
        {screenState === "saisieUsager" && (
          <UsernameScreen
            setScreenStateFn={setScreenState}
            setUserNameFn={setUserName}
            username={username}
            setUsername={(value) => {
              setUsername(value);
              setSsoErrorMsg("");
            }}
            initialError={ssoErrorMsg}
          />
        )}
        {screenState === "saisieMotDePasse" && (
          <PasswordScreen
            setScreenStateFn={setScreenState}
            setOrganizations={setOrganizations}
            setUserNameFn={setUserName}
            goToHomePageFn={goToHomePage}
            onMfaRequired={handleMfaRequired}
            username={username}
          />
        )}
        {screenState === "mfa" && (
          <MfaScreen
            mfaChallengeToken={mfaChallengeToken}
            setScreenStateFn={setScreenState}
            setOrganizations={setOrganizations}
            goToHomePageFn={goToHomePage}
            onClearMfa={handleClearMfa}
          />
        )}
        {screenState === "changePassword" && <ChangePasswordScreen setScreenStateFn={setScreenState} />}
        {screenState === "selectOrganization" && (
          <SelectOrganizationScreen setScreenStateFn={setScreenState} organizations={organizations} goToHomePageFn={goToHomePage} />
        )}
        {screenState === "resetPassword" && <ResetPasswordScreen setScreenStateFn={setScreenState} />}
      </div>
    </div>
  );
};

export default U2001Login;
